import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconArrowUpRight, IconArticle } from "@tabler/icons-react";
import BookMarkFillIcon from "../../../assets/icons/bookmark-fill.svg?react";
import BookMarkSimpleIcon from "../../../assets/icons/bookmark-simple.svg?react";
import ShareV2Icon from "../../../assets/icons/share-v2.svg?react";
import PlaylistPanel from "../../../components/app/PlaylistPanel";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import {
  DesktopArticleRow,
  DesktopArticleRowSkeleton,
} from "../../../components/discovery/DesktopArticleRow";
import { RailSectionTitle } from "../../../components/discovery/DesktopExploreRail";
import { RoomJoinOffer } from "../../../components/room/DiscoverableRoomBlock";
import { DesktopRoomSource } from "../../../components/room/DesktopRoomSource";
import { DesktopSaveButton } from "../../../components/save/DesktopSaveButton";
import Skeleton from "../../../components/skeleton/Skeleton";
import { Button } from "../../../components/ui/Button";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { getArticleTypeVariant } from "../../../domains/app/utils/getArticleTypeText";
import {
  formatArticleDate,
  formatShortDate,
} from "../../../domains/app/utils/renderDate";
import { authorLine } from "../../../domains/article/utils/authorLine";
import {
  cardsOf,
  useGetRoomResourceItemsQuery,
  type RoomResourceListItem,
} from "../../../domains/room/hooks/useGetRoomResourceItemsQuery";
import { resourceItemKey } from "../../../../../api/src/shared/domain/room-resources";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { useSharedContentBack } from "../../../domains/room/hooks/useSharedContentBack";
import type { PrivateRoom } from "../../../domains/room/privateRoom";
import { getRoomLogoUrl } from "../../../domains/room/utils/getRoomLogoUrl";
import {
  roomResourceDate,
  roomResourceTarget,
  roomResourceTitle,
} from "../../../domains/room/utils/roomResourceItem";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { openExternalUrl } from "../../../tools/open-external-url";
import { isB2bOrganisation } from "../../../../../api/src/shared/domain/organisation";
import { resourceTypeLabel } from "../../room/desktop/DesktopRoomRail";
import { VideoPlayer } from "../VideoPlayer";
import { formatVideoDuration, useVideoPage } from "../useVideoPage";
import { SourceLabel } from "../../../components/ui/SourceLabel";

/** How many of the room's other resources the rail lists. */
const RAIL_RESOURCE_COUNT = 3;

/**
 * A video on a desktop (Figma "02 - Explore / Vidéo & Infographie",
 * "D3.video.default"): the player across the whole 1032px block, so the slides
 * of a talk stay readable, then the 680px column (title, source, actions,
 * recommended publications) beside the room's other resources in the rail.
 */
export function DesktopVideoPage({
  slug,
  roomId,
}: {
  slug: string;
  roomId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { activeLang, contentLang } = useActiveLang();
  const { articleLink } = usePageLinks(roomId);
  const handleBack = useSharedContentBack(roomId);
  const {
    contentQuery,
    video,
    currentRoom,
    relatedArticles,
    isSaved,
    toggleSaveToPlaylists,
    shareVideo,
    playlistPanel,
  } = useVideoPage({ slug, roomId });

  const topNav = (
    <DesktopTopNav
      context={
        <DesktopBreadcrumb
          back={{ kind: "separate", onBack: handleBack }}
          // A video is a resource of its room: the nearest crumb returns to
          // the room's Resources.
          trail={
            currentRoom === null
              ? [{ label: t("Explore"), to: "/discovery" }]
              : [
                  { label: t("Rooms"), to: "/rooms" },
                  {
                    label: getRoomName(currentRoom, contentLang),
                    to: `/room/resources/${currentRoom.id}`,
                  },
                ]
          }
          current={t("Video")}
        />
      }
      trailing={{ kind: "search" }}
      surface="default"
      className={null}
    />
  );

  if (contentQuery.state === "error") {
    return (
      <div className="flex h-full flex-col overflow-y-auto">
        {topNav}
        <p className="m-0 px-10 pt-8 text-ui-body text-ds-slate-600">
          {t("Something went wrong. Please try again.")}
        </p>
      </div>
    );
  }

  const associatedArticle =
    video === null ? undefined : video.associatedArticles.at(0);
  const externalLink = video === null ? null : video.externalLink;
  const meta =
    video === null
      ? null
      : [
          t("Video"),
          ...(video.duration === null
            ? []
            : [formatVideoDuration(video.duration)]),
          ...(video.publication_date === null
            ? []
            : [formatShortDate(video.publication_date, activeLang)]),
        ].join(" · ");

  return (
    <div className="flex h-full flex-col overflow-y-auto font-dm-sans">
      {topNav}
      <div className="px-10 pb-24">
        <div className="mx-auto flex max-w-desktop-block flex-col gap-8 pt-8">
          {/* The frame is laid out before the video loads: the page does not
              jump when the player arrives. */}
          <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-ds-slate-100">
            {video === null ? null : (
              <VideoPlayer
                video={video}
                failureView={{
                  kind: "panel",
                  title: t("This video can't be played right now."),
                  description: t("Check your connection, then try again."),
                  retryLabel: t("Try again"),
                }}
              />
            )}
          </div>

          <div className="flex items-start gap-8">
            <div className="flex max-w-desktop-column min-w-0 flex-1 flex-col gap-6">
              {video === null || meta === null ? (
                <HeaderSkeleton />
              ) : (
                <header className="flex flex-col gap-3">
                  <p className="m-0 text-desktop-meta text-ds-slate-600">
                    {meta}
                  </p>
                  <h1 className="m-0 text-desktop-article-title font-semibold text-ds-black">
                    {video.title}
                  </h1>
                  {video.journal === null ? null : (
                    <p className="m-0 text-ui-body">
                      <SourceLabel
                        source={{ kind: "in", name: video.journal.name }}
                        onNameClick={null}
                      />
                    </p>
                  )}
                  {currentRoom === null ? null : (
                    <DesktopRoomSource room={currentRoom} />
                  )}
                </header>
              )}

              {/* Under the title: the reader sees what they were sent first. */}
              {roomId === null ? null : <RoomJoinOffer roomId={roomId} />}

              {video === null ? null : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    {associatedArticle === undefined ? null : (
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<IconArticle stroke={1.5} />}
                        onClick={() => {
                          history.push(articleLink(associatedArticle));
                        }}
                      >
                        {t("Read the linked article")}
                      </Button>
                    )}
                    {externalLink === null ? null : (
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<IconArrowUpRight stroke={1.5} />}
                        onClick={() => {
                          openExternalUrl(externalLink);
                        }}
                      >
                        {t("Find out more")}
                      </Button>
                    )}
                  </div>
                  {currentUser === null ? null : (
                    <div className="flex shrink-0 items-center gap-1">
                      <IconAction
                        label={t("Share")}
                        onClick={() => {
                          void shareVideo(video, currentUser, roomId);
                        }}
                      >
                        <ShareV2Icon />
                      </IconAction>
                      <IconAction
                        label={isSaved ? t("Saved") : t("Save")}
                        onClick={() => {
                          void playlistPanel.open(video.id);
                        }}
                      >
                        {isSaved ? (
                          <BookMarkFillIcon />
                        ) : (
                          <BookMarkSimpleIcon />
                        )}
                      </IconAction>
                    </div>
                  )}
                </div>
              )}

              <RecommendedPublications
                articles={relatedArticles}
                articleLink={articleLink}
              />
            </div>

            {currentRoom === null || video === null ? null : (
              <aside className="sticky top-20 hidden w-desktop-rail shrink-0 flex-col gap-6 lg:flex">
                <RoomResourcesRail room={currentRoom} currentId={video.id} />
              </aside>
            )}
          </div>
        </div>
      </div>

      {playlistPanel.state.status === "ready" && video !== null ? (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({ add, remove }) => {
            await toggleSaveToPlaylists(video, { add, remove });
          }}
        />
      ) : null}
    </div>
  );
}

function IconAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex size-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-black transition-colors hover:bg-ds-slate-100 [&>svg]:size-5"
    >
      {children}
    </button>
  );
}

function RecommendedPublications({
  articles,
  articleLink,
}: {
  articles: ReturnType<typeof useVideoPage>["relatedArticles"];
  articleLink: ReturnType<typeof usePageLinks>["articleLink"];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  if (articles !== null && articles.length === 0) return null;
  return (
    <section className="flex flex-col gap-2 border-t border-ds-slate-100 pt-6">
      <h2 className="m-0 text-desktop-heading font-semibold text-ds-black">
        {t("Recommended publications")}
      </h2>
      <div className="flex flex-col divide-y divide-ds-slate-100">
        {articles === null
          ? Array.from({ length: 3 }).map((_, idx) => (
              <DesktopArticleRowSkeleton key={idx} />
            ))
          : articles.map((article) => {
              const cardImage = article.cardImage;
              return (
                <DesktopArticleRow
                  key={article.id}
                  layout="row"
                  title={article.title}
                  source={
                    article.journal === null
                      ? null
                      : {
                          kind: "journal",
                          name: article.journal.name,
                          impactFactor: null,
                          onClick: null,
                        }
                  }
                  author={authorLine(article.primary_author, article.authors)}
                  description={null}
                  date={formatArticleDate(article.publication_date, activeLang)}
                  readingMinutes={null}
                  meta={
                    article.article_type === null
                      ? t("Article")
                      : article.article_type
                  }
                  metaVariant={getArticleTypeVariant(article.article_type)}
                  access={article.isOpenAccess === true ? "open" : "closed"}
                  imageUrl={
                    cardImage === null
                      ? null
                      : cardImage.sizes === null
                        ? cardImage.url
                        : cardImage.sizes.medium.file
                  }
                  isInPlayer={false}
                  bookmark={
                    <DesktopSaveButton
                      article={article}
                      analyticsListName={AnalyticsItemList.VIDEO_ARTICLE_PAGE}
                      size="row"
                    />
                  }
                  onShare={null}
                  onRemove={null}
                  onClick={() => {
                    history.push(articleLink(article));
                  }}
                />
              );
            })}
      </div>
    </section>
  );
}

/**
 * The room's other resources (Figma "DesktopRail / Shared by [Partner]"): the
 * room, its partner's mention, the first resources that open a page, and the
 * way to all of them in the room's Resources.
 */
function RoomResourcesRail({
  room,
  currentId,
}: {
  room: PrivateRoom;
  currentId: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang, contentLang } = useActiveLang();
  const resourcesQuery = useGetRoomResourceItemsQuery(room.id, "include");
  const isPartnerRoom = isB2bOrganisation(room.organisationModel.id);
  if (resourcesQuery.state === "error") return null;
  const items =
    resourcesQuery.state === "pending"
      ? null
      : cardsOf(resourcesQuery.items)
          .filter((item) => resourceItemKey(item) !== `content-${currentId}`)
          .flatMap((item) => {
            const target = roomResourceTarget(item, room.id);
            return target.kind === "page" ? [{ item, path: target.path }] : [];
          })
          .slice(0, RAIL_RESOURCE_COUNT);
  if (items !== null && items.length === 0) return null;
  return (
    <section className="flex flex-col gap-4 rounded-xl border border-ds-slate-100 bg-ds-white p-4">
      <div className="flex items-center gap-2.5">
        <img
          src={getRoomLogoUrl(room)}
          alt=""
          className="size-6 shrink-0 rounded-full object-contain"
        />
        <div className="min-w-0 flex-1">
          <RailSectionTitle
            title={getRoomName(room, contentLang)}
            onSeeAll={null}
          />
        </div>
      </div>
      {isPartnerRoom ? (
        <p className="m-0 text-desktop-meta text-ds-slate-600">
          {t("Shared by {{organisation}}", {
            organisation: room.organisationModel.name,
          })}
        </p>
      ) : null}
      <ul className="m-0 flex list-none flex-col gap-4 p-0">
        {items === null
          ? Array.from({ length: RAIL_RESOURCE_COUNT }).map((_, idx) => (
              <li key={idx} className="flex flex-col gap-1">
                <Skeleton className="rounded" width="40%" height={14} />
                <Skeleton className="rounded" width="90%" height={18} />
              </li>
            ))
          : items.map(({ item, path }) => (
              <li key={resourceItemKey(item)}>
                <RailResource
                  item={item}
                  label={`${resourceTypeLabel(item, t)} · ${roomResourceDate(item, activeLang)}`}
                  onClick={() => {
                    history.push(path);
                  }}
                />
              </li>
            ))}
      </ul>
      <Link
        to={`/room/resources/${room.id}`}
        className="text-footnote font-medium text-ds-mint-800 no-underline hover:underline"
      >
        {t("See all")}
      </Link>
    </section>
  );
}

function RailResource({
  item,
  label,
  onClick,
}: {
  item: RoomResourceListItem;
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer flex-col gap-0.5 border-none bg-transparent p-0 text-left"
    >
      <span className="text-desktop-meta text-ds-slate-600">{label}</span>
      <span className="line-clamp-2 text-footnote font-semibold text-ds-black hover:underline">
        {roomResourceTitle(item)}
      </span>
    </button>
  );
}

/** The title block while the video loads: meta, two title lines, source. */
function HeaderSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="rounded" width={180} height={14} />
      <Skeleton className="rounded" width="90%" height={32} />
      <Skeleton className="rounded" width="60%" height={32} />
      <Skeleton className="rounded" width={240} height={18} />
    </div>
  );
}
